import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useLocation } from "react-router-dom";

import { useDriverTracking } from "../hooks/useDriverTracking";
import { useOrderTracking } from "../hooks/useOrderTracking";
import { useDeliveryInstructions } from "../hooks/useDeliveryInstructions";
import { useSignalR } from "../hooks/useSignalR";
import { useCarousel } from "../hooks/useCarousel";
import { useMapTracking } from "../hooks/useMapTracking";
import { useDeliveryUI } from "../hooks/useDeliveryUI";
import { useRating } from "../hooks/useRating";
import { useDriverRemainingTime } from "../hooks/useDriverRemainingTime";
import { useDriverTrackerStatus } from "../hooks/useDriverTrackerStatus";

import {
  ChevronUp,
  ChevronDown,
  Phone,
  Timer,
  ShieldCheck,
  CheckCircle,
  ClipboardList,
  Copy,
  Check,
  ThumbsUp,
  Plus,
} from "lucide-react";

import "leaflet/dist/leaflet.css";
import banner1 from "../Assets/banner_3.webp";
import banner2 from "../Assets/banner_4.webp";
import item1 from "../Assets/Food.svg";
import home from "../Assets/Home.svg";
import resturant from "../Assets/Restaurant icon.svg";
import deliveryman from "../Assets/delivery man 512 x 512.svg";
import { toast } from "sonner";
import OrderTrackingMap from "./OrderTrackingMap";
import DeliveryInstructions from "./DeliveryInstructions";
import OrderSummary from "./OrderSummary";
import DeliveryRating from "./DeliveryRating";

export default function OrderTracking() {
  const renderCount = useRef(0);
  renderCount.current += 1;

  const { taskId: rawParam } = useParams();
  const taskId = rawParam?.split("-")?.[0];
  const orderId = rawParam?.split("-")?.[1]
  ;
  const location = useLocation();
const isAdminView = location.pathname.endsWith("/admin");


  const {
    driverStatus: trackerDriverStatus,
    merchantStatus: trackerMerchantStatus,
    refetchStatus: fetchTrackerStatus,
  } = useDriverTrackerStatus(taskId);

  const isDelivered = trackerDriverStatus === "successful";
  const normalizedStatus = trackerDriverStatus?.toLowerCase()?.trim();

  const isDriverVisible = [
    "acknowledged",
    "arrived",
    "food is ready",
    "picked up",
    "successful",
  ].includes(normalizedStatus);

  const {
    driverData,
    driverPosition,
    remainingTime: driverRemainingTime,
    totalTime: driverTotalTime,
    acknowledgeTime,
    refetchDriver,
  } = useDriverTracking(taskId, isDriverVisible);

  const assignedDriverCode =
    driverData?._driverCode ||
    driverData?.driverCode ||
    driverData?.driverID ||
    null;

  // console.log(" Assigned Driver Code from API:", assignedDriverCode);

  const { remainingTime: apiRemainingTime, fetchRemainingTime } =
    useDriverRemainingTime(isDelivered ? null : taskId);

  const [liveDriverPosition, setLiveDriverPosition] = useState(null);

  const finalDriverPosition = liveDriverPosition ?? driverPosition ?? null;
  // console.log(" FINAL DRIVER POSITION:", finalDriverPosition);

  const { orderDetails, products, pricing, fetchOrderItems } =
    useOrderTracking(taskId);

  const {
    instructionList,
    submittedInstruction,
    existingInstruction,
    fetchInstructions,
    submitInstruction,
  } = useDeliveryInstructions(taskId);

  const fetchDeliveryInstructions = fetchInstructions;

  const fetchOrderDetails = fetchOrderItems;

  const {
    index,
    images: loopedImages,
    enableTransition,
  } = useCarousel([banner1, banner2], 3000);

  const merchantStatus =
    trackerMerchantStatus ?? orderDetails?.merchant_status?.toLowerCase();

  const driverPhase = useMemo(() => {
    if (trackerDriverStatus === "successful") {
      return "DELIVERED";
    }

    if (
      trackerDriverStatus === "unassigned" &&
      merchantStatus === "unassigned"
    ) {
      return "NOT_STARTED";
    }

    if (trackerDriverStatus === "assigned" && merchantStatus === "unassigned") {
      return "Assigned";
    }

    if (
      trackerDriverStatus === "acknowledged" &&
      merchantStatus === "unassigned"
    ) {
      return "Acknowledged";
    }

    if (
      trackerDriverStatus === "acknowledged" &&
      merchantStatus === "food is ready"
    ) {
      return "On the way to restaurant";
    }

    if (
      trackerDriverStatus === "arrived" &&
      merchantStatus !== "food is ready"
    ) {
      return "NOT_STARTED_PREPARING";
    }

    if (
      trackerDriverStatus === "arrived" &&
      merchantStatus === "food is ready"
    ) {
      return "STANDING_IN_RESTAURANT";
    }

    if (
      trackerDriverStatus === "picked up" &&
      merchantStatus === "food is ready"
    ) {
      return "ON_THE_WAY";
    }

    return "Please wait";
  }, [trackerDriverStatus, merchantStatus]);

  const map = useMapTracking({
    orderDetails,
    driverPosition: finalDriverPosition,
    merchantStatus,
    driverPhase,
    assets: {
      restaurant: resturant,
      home,
      driver: deliveryman,
    },
  });

  const { pickupLat, pickupLng, homeLat, homeLng, formattedStoreDistance } =
    map;

  const handleSubmitRating = () => {
    submitDeliveryRating({ orderDetails });
  };

  const { iconMap, deliveryDetails } = useDeliveryUI({
    orderDetails,
  });

  const effectiveRemainingTime = isDelivered
    ? 0
    : Number.isFinite(apiRemainingTime)
      ? apiRemainingTime
      : null;

  // console.log(" EFFECTIVE REMAINING TIME:", effectiveRemainingTime);

  /*  UI STATE  */

  const [expand, setExpand] = React.useState(true);
  const [manualExpand, setManualExpand] = React.useState(false);
  const [showSafety, setShowSafety] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [displayRemainingTime, setDisplayRemainingTime] = useState(null);
  const [isMapAnimating, setIsMapAnimating] = useState(false);
  const [currenttaskv1, setCurrenttaskv1] = useState(null);

  useEffect(() => {
    if (!taskId) return;

    // Fetch initial data on mount
    fetchTrackerStatus();

    if (isDriverVisible) {
      refetchDriver();
    }

    // Fetch remaining time if driver is acknowledged
    if (
      !isDelivered &&
      ["acknowledged", "arrived", "food is ready"].includes(trackerDriverStatus)
    ) {
      fetchRemainingTime();
    }
  }, [taskId]);

  const signalIntervalRef = useRef(null);
  const hasFetchedOnAckRef = useRef(false);
  const lockedDriverIdRef = useRef(null);

  const sendMessageRef = useRef(null);
  const fetchRemainingTimeRef = useRef(null);

  const prevStatusRef = useRef();
  const prevConnectedRef = useRef();
  const prevAckTimeRef = useRef();
  const prevTotalTimeRef = useRef();
  const hasSignalREtaRef = useRef(false);
  const hasApiEtaRef = useRef(false);

  const extractRemainingTimeFromSignalR = (message = "") => {
    // Match both positive and negative numbers
    const match = message.match(/remaining\s*time\s*(-?\d+)/i);
    if (!match) return null;
    const value = Number(match[1]);
    if (!Number.isFinite(value)) return null;

    return value;
  };

  const extractLatLngFromSignalR = (message = "") => {
    const match = message.match(
      /Latitude\s*=\s*([0-9.-]+)\s*and\s*Longitude\s*=\s*([0-9.-]+)/i,
    );

    if (!match) return null;

    return [Number(match[1]), Number(match[2])];
  };

  const parseLatLng = (msg) => {
    const match = msg.match(
      /Latitude\s*=\s*([0-9.-]+)\s*and\s*Longitude\s*=\s*([0-9.-]+)/i,
    );
    if (!match) return null;
    return [Number(match[1]), Number(match[2])];
  };

  const restaurantName = orderDetails?.merchant_name || "Restaurant";
  const instructionSubmitted = !!submittedInstruction;

  const isDriverOnTheWay = useMemo(() => {
    const result =
      merchantStatus === "food is ready" &&
      ["acknowledged", "arrived", "picked up"].includes(trackerDriverStatus);
    return result;
  }, [merchantStatus, trackerDriverStatus]);

  console.log("TRACKER STATUS:", trackerDriverStatus);
  console.log("MERCHANT STATUS:", merchantStatus);

  const {
    driverRating,
    setDriverRating,
    customerMessage,
    setCustomerMessage,
    ratingSubmitting,
    ratingSubmitted,
    existingRating,
    loadingRating,
    submitDeliveryRating,
    ratingDismissed,
    dismissRating,
  } = useRating(taskId, isDelivered);

  const driverLat = finalDriverPosition?.[0];
  const driverLng = finalDriverPosition?.[1];

  const hasDriverLocation =
    Number.isFinite(driverLat) && Number.isFinite(driverLng);

  // console.log(" hasDriverLocation:", hasDriverLocation);

  const hasDriverStartedMoving = useMemo(() => {
    const result =
      isDriverOnTheWay &&
      trackerDriverStatus !== "arrived" &&
      Number.isFinite(driverLat) &&
      Number.isFinite(driverLng);
    // console.log(" hasDriverStartedMoving COMPUTED:", result);
    return result;
  }, [isDriverOnTheWay, trackerDriverStatus, driverLat, driverLng]);

  const signalRTotalTime = useMemo(() => {
    if (isDelivered) return 0;

    if (Number.isFinite(driverTotalTime) && driverTotalTime > 0) {
      return driverTotalTime;
    }

    return null; //  not ready yet
  }, [driverTotalTime, isDelivered]);

  useEffect(() => {
    if (isDelivered) {
      setDisplayRemainingTime(0);
      return;
    }

    //  If SignalR has provided ETA, don't override it
    if (hasSignalREtaRef.current && Number.isFinite(displayRemainingTime)) {
      return;
    }

    //  Set API ETA when it arrives
    if (Number.isFinite(apiRemainingTime)) {
      hasApiEtaRef.current = true;
      console.log(" Setting API ETA:", apiRemainingTime);
      setDisplayRemainingTime(Number(apiRemainingTime));
      return;
    }

    //  Fallback only if API was tried and returned nothing
    if (
      hasApiEtaRef.current &&
      !Number.isFinite(apiRemainingTime) &&
      displayRemainingTime == null
    ) {
      // console.log(" Using fallback ETA: 20");
      setDisplayRemainingTime(20);
    }
  }, [isDelivered, apiRemainingTime, displayRemainingTime]);

  const etaText = useMemo(() => {
    if (!Number.isFinite(displayRemainingTime)) {
      return null;
    }

    if (displayRemainingTime <= 0) {
      return {
        text: "Your delivery partner is at your doorstep",
        color: "text-green-600",
      };
    }

    if (displayRemainingTime <= 2) {
      return {
        text: "Your delivery partner is just a few moments away",
        color: "text-orange-600",
      };
    }

    return {
      text: `${displayRemainingTime} min away`,
      color: "text-gray-600",
    };
  }, [displayRemainingTime]);

  const trackingText = useMemo(() => {
    switch (driverPhase) {
      case "NOT_STARTED":
        return {
          sub: "Please wait a movement",
          main: "We’re assigning your order to a delivery partner right now",
        };

      case "Assigned":
        return {
          sub: "Delivery partner assigned",
          main: "Waiting for the driver to accept your order",
        };

      case "Acknowledged":
        return {
          sub: "Driver accepted your order",
          main: "Your delivery partner is on the way to the restaurant",
        };

      case " Not Started Preparing":
        return {
          sub: "Driver reached the restaurant",
          main: "Your order is being freshly prepared",
        };

      case "On the way to restaurant":
        return {
          sub: "Food is ready",
          main: "Driver is heading to pick up your order",
        };

      case "STANDING_IN_RESTAURANT":
        return {
          sub: "Food is ready at the restaurant",
          main: "Your delivery partner is waiting to pick up your order",
        };

      case "ON_THE_WAY": {
        return {
          sub: "Order picked up",
          main: "Driver is on the way",
        };
      }

      case "DELIVERED":
        return {
          sub: "Delivery completed",
          main: "Your order has been delivered. Enjoy your meal!",
        };

      default:
        return {
          sub: "Please wait",
          main: "We’re preparing your order",
        };
    }
  }, [driverPhase]);

  useEffect(() => {
    if (Number.isFinite(apiRemainingTime)) {
      hasApiEtaRef.current = true;
    }
  }, [apiRemainingTime]);

  const showRestaurant =
    !hasDriverLocation || merchantStatus !== "food is ready";

  const showHome = hasDriverLocation && merchantStatus === "food is ready";

  const showDriver = hasDriverLocation;

  const handleMapToggle = () => {
    setManualExpand(true);
    setIsMapAnimating(true);

    // Animation start
    setTimeout(() => {
      setExpand((prev) => !prev);
    }, 50);

    // Animation end (900ms total)
    setTimeout(() => {
      setIsMapAnimating(false);
    }, 950);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(orderId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const showRatingSection =
    trackerDriverStatus === "successful" ||
    merchantStatus === "delivered" ||
    merchantStatus === "successful";

  const handleSignalMessage = useCallback(
    (data) => {
      // console.log(" SignalR RAW:", data);
      const msg = data?.message?.toLowerCase() || "";

      /* STATUS EVENTS */
      const isStatusEvent =
        msg.includes("status changed") ||
        msg.includes("unassigned") ||
        msg.includes("assigned") ||
        msg.includes("acknowledged") ||
        msg.includes("arrived") ||
        msg.includes("food is ready") ||
        msg.includes("successful");

      if (isStatusEvent) {
        // console.log(" STATUS EVENT  fetchTrackerStatus()");
        fetchTrackerStatus();
        return;
      }

      /* ETA EVENTS */
      if (msg.includes("remaining time") || msg.includes("remaining_time")) {
        // Use task ID for ETA (this is correct as ETA comes with task ID)
        const signalTaskId = String(data?.driverId || "").trim();

        if (!lockedDriverIdRef.current && assignedDriverCode) {
          lockedDriverIdRef.current = assignedDriverCode;
          // console.log(" Driver locked for ETA (from API):", assignedDriverCode);
        }

        // For ETA, we compare task ID
        if (signalTaskId !== taskId) {
          // console.log(" Ignoring ETA from other task:", signalTaskId);
          return;
        }

        const remaining = extractRemainingTimeFromSignalR(msg);
        if (Number.isFinite(remaining)) {
          // console.log(" SignalR ETA received:", remaining);

          const displayValue = remaining < 0 ? 0 : remaining;

          hasSignalREtaRef.current = true;
          setDisplayRemainingTime(displayValue);

          // console.log(" Display ETA set to:", displayValue);
        }
        return;
      }

      /* LOCATION EVENTS */
      // console.log(" Checking for latitude in message:", msg.includes("latitude"));

      if (!msg.includes("latitude")) {
        // console.log(" No latitude found in message, skipping location update");
        return;
      }

      // console.log(" Latitude found in message - processing location update");
      // console.log(" Current trackerDriverStatus:", trackerDriverStatus);
      // console.log(" Current driverData:", driverData);
      // console.log(" Assigned Driver Code (API):", assignedDriverCode);

      const isDriverAssigned = [
        "acknowledged",
        "arrived",
        "food is ready",
        "picked up",
        "successful",
      ].includes(normalizedStatus);

      // console.log(" Is driver assigned?", isDriverAssigned);

      if (!isDriverAssigned) {
        // console.log(" Driver not acknowledged yet, ignore location");
        // console.log("   Current status:", trackerDriverStatus);
        return;
      }

      if (!driverData || !assignedDriverCode) {
        // console.log(" Driver data not loaded yet or no driver code");
        // console.log("   driverData:", driverData);
        // console.log("   assignedDriverCode:", assignedDriverCode);
        return;
      }

      // console.log(" Extracting lat/lng from message...");
      const latLng = extractLatLngFromSignalR(msg);

      if (!latLng) {
        // console.log(" Failed to extract lat/lng from message");
        // console.log("   Message was:", data?.message);
        return;
      }

      // console.log(" Successfully extracted lat/lng:", latLng);

      // CRITICAL: Get driver code from SignalR (NOT task ID!)
      const signalDriverCode = String(data?.driverId || "").trim();
      // console.log(" SignalR Driver Code:", signalDriverCode);
      // console.log(" Assigned Driver Code:", assignedDriverCode);
      // console.log(" Locked Driver Code:", lockedDriverIdRef.current);

      // Lock to API driver code on first location event
      if (!lockedDriverIdRef.current) {
        lockedDriverIdRef.current = assignedDriverCode;
        // console.log(" Driver locked for location (from API):", assignedDriverCode);
      }

      // CRITICAL: Only accept location from assigned driver
      if (signalDriverCode !== assignedDriverCode) {
        // console.log(" REJECTING location from different driver");
        // console.log("   SignalR Driver Code:", signalDriverCode);
        // console.log("   Assigned Driver Code:", assignedDriverCode);
        // console.log("   This is NOT our driver - location update REJECTED");
        return;
      }

      // console.log(" Driver code matches - updating location");

      // Update location with detailed logging
      setLiveDriverPosition((prev) => {
        // console.log(" DRIVER LOCATION UPDATE");
        // console.log(" Previous Driver Location:");
        // console.log("   Latitude:", prev?.[0] || "N/A");
        // console.log("   Longitude:", prev?.[1] || "N/A");
        // console.log("   Full:", prev);
        // console.log(" New Driver Location:");
        // console.log("   Latitude:", latLng[0]);
        // console.log("   Longitude:", latLng[1]);
        // console.log("   Full:", latLng);
        // console.log(" Update Info:");
        // console.log("   Driver Code (SignalR):", signalDriverCode);
        // console.log("   Driver Code (Assigned):", assignedDriverCode);
        // console.log("   Task ID:", taskId);
        // console.log("   Timestamp:", new Date().toLocaleTimeString());

        return latLng;
      });
    },
    [
      driverData,
      assignedDriverCode,
      fetchTrackerStatus,
      trackerDriverStatus,
      taskId,
    ],
  );

  const { sendMessage, isConnected } = useSignalR({
    url: "https://api2.packky.com/notificationHub",
    enabled: Boolean(taskId),
    onMessage: handleSignalMessage,
  });

  // reset taskid
  useEffect(() => {
    lockedDriverIdRef.current = null;
    setLiveDriverPosition(null);
  }, [taskId]);

  // Status change watcher
  useEffect(() => {

    if (
      !trackerDriverStatus ||
      ["unassigned", "assigned"].includes(trackerDriverStatus)
    ) {
      hasFetchedOnAckRef.current = false;
      setLiveDriverPosition(null);
      lockedDriverIdRef.current = null;
      hasSignalREtaRef.current = false;
      hasApiEtaRef.current = false;
      setDisplayRemainingTime(null);
      return;
    }

    // Fetch remaining time when driver status changes to active state
    if (
      !isDelivered &&
      ["acknowledged", "arrived", "food is ready", "picked up"].includes(
        trackerDriverStatus,
      )
    ) {
      fetchRemainingTime();
    }
  }, [trackerDriverStatus, isDelivered, fetchRemainingTime]);

  //  MAIN INTERVAL EFFECT
  useEffect(() => {
    // Skip if values haven't changed
    const valuesChanged =
      prevStatusRef.current !== trackerDriverStatus ||
      prevConnectedRef.current !== isConnected ||
      prevAckTimeRef.current !== acknowledgeTime ||
      prevTotalTimeRef.current !== signalRTotalTime;

    if (!valuesChanged && signalIntervalRef.current) {
      return;
    }

    // Update refs
    prevStatusRef.current = trackerDriverStatus;
    prevConnectedRef.current = isConnected;
    prevAckTimeRef.current = acknowledgeTime;
    prevTotalTimeRef.current = signalRTotalTime;

    if (!isConnected) {
      console.log(" Not connected to SignalR");
      return;
    }

    // STOP if delivered
    if (trackerDriverStatus === "successful") {
      if (signalIntervalRef.current) {
        clearInterval(signalIntervalRef.current);
        signalIntervalRef.current = null;
        console.log(" SignalR STOPPED → successful");
      }
      return;
    }

    // INVALID STATES
    if (
      !trackerDriverStatus ||
      ["assigned", "unassigned"].includes(trackerDriverStatus)
    ) {
      if (signalIntervalRef.current) {
        clearInterval(signalIntervalRef.current);
        signalIntervalRef.current = null;
        console.log(" SignalR STOPPED → invalid status");
      }
      return;
    }

    // VALID STATES CHECK
    if (
      !["acknowledged", "arrived", "food is ready", "picked up"].includes(
        trackerDriverStatus,
      )
    ) {
      return;
    }

    if (!Number.isFinite(signalRTotalTime)) {
      return;
    }

    if (signalIntervalRef.current) {
      return;
    }

    const sendUpdate = () => {
      const payload = {
        method: "UpdateDriverLocation",
        taskId: String(taskId),
        acknowledgeTime: acknowledgeTime ?? "0",
        totalTime: String(signalRTotalTime),
      };

      console.log(" SignalR SEND", payload);

      sendMessageRef.current?.(
        payload.method,
        payload.taskId,
        payload.acknowledgeTime,
        payload.totalTime,
      );

      fetchRemainingTimeRef.current?.();
    };

    sendUpdate();
    signalIntervalRef.current = setInterval(sendUpdate, 30000);

    return () => {
      console.log(" SignalR CLEANUP");
      if (signalIntervalRef.current) {
        clearInterval(signalIntervalRef.current);
        signalIntervalRef.current = null;
      }
    };
  }, [
    isConnected,
    trackerDriverStatus,
    taskId,
    acknowledgeTime,
    signalRTotalTime,
  ]);

  useEffect(() => {
    if (taskId) {
      setCurrenttaskv1(String(taskId));
    }
  }, [taskId]);

  useEffect(() => {
    if (!hasDriverStartedMoving) return;
    if (!Number.isFinite(displayRemainingTime)) return;
    if (displayRemainingTime <= 0) return;
  }, [hasDriverStartedMoving, displayRemainingTime]);

  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  useEffect(() => {
    fetchRemainingTimeRef.current = fetchRemainingTime;
  }, [fetchRemainingTime]);

  // for map section
  useEffect(() => {
    if (manualExpand) return;

    if (isDelivered) {
      setExpand(false);
      return;
    }

    if (hasDriverStartedMoving) {
      setExpand(true);
      return;
    }

    setExpand(false);
  }, [manualExpand, isDelivered, hasDriverStartedMoving]);

  useEffect(() => {
    setManualExpand(false);
  }, [taskId]);

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center">
      <div className="w-full max-w-md bg-orange-100 relative">
    
        {/* MAP SECTION */}
        {!isDelivered && (
          <OrderTrackingMap
            pickupLat={pickupLat}
            pickupLng={pickupLng}
            homeLat={homeLat}
            homeLng={homeLng}
            driverLat={driverLat}
            driverLng={driverLng}
            restaurantIcon={resturant}
            homeIcon={home}
            driverIcon={deliveryman}
            trackingText={trackingText}
            etaText={etaText}
            isOrderPickedUp={
              trackerDriverStatus === "picked up" ||
              driverPhase === "ON_THE_WAY"
            }
            displayRemainingTime={displayRemainingTime}
          />
        )}

        {/* DELIVERY PARTNER */}
        {trackerDriverStatus &&
          ["acknowledged", "arrived"].includes(trackerDriverStatus) && (
            <div className="bg-white max-w-md mt-4 rounded-xl shadow p-4 mx-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200">
                  <img
                    src={driverData?.image}
                    alt="Driver"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex-1">
                  <p className="font-semibold">
                    I'm {driverData?.name || "your delivery partner"} your
                    delivery partner
                  </p>

                  <p className="text-sm text-gray-600">{trackingText?.main}</p>
                </div>

                {driverData?.phone && (
                  <a
                    href={`tel:${driverData.phone}`}
                    className="p-2 rounded-full border"
                  >
                    <Phone size={18} className="text-orange-600" />
                  </a>
                )}
              </div>
            </div>
          )}

        {/* STORE DISTANCE */}

        <div className="bg-white max-w-md mt-4 mx-4 rounded-xl p-4">
          {/* DELIVERED STATE */}
          {trackerDriverStatus === "successful" ? (
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>

              <p className="text-sm text-gray-700 flex-1">
                Your order from{" "}
                <strong className="text-green-700">{restaurantName}</strong> has
                been successfully delivered
              </p>
            </div>
          ) : (
            <>
              {/* ONGOING DELIVERY STATE */}
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-gray-100">
                  <ShieldCheck className="w-6 h-6 text-green-500" />
                </div>

                <p className="text-sm text-gray-700 flex-1">
                  <strong>{restaurantName}</strong>{" "}
                  {formattedStoreDistance && (
                    <>
                      is <strong>{formattedStoreDistance}</strong> away from
                      your address.
                    </>
                  )}
                  <br />
                  <span className="text-orange-600 text-sm">
                    Learn about delivery partner safety
                  </span>
                </p>

                <button
                  onClick={() => setShowSafety(!showSafety)}
                  className="p-3 rounded-full ml-auto bg-gray-100"
                >
                  {showSafety ? (
                    <ChevronUp size={18} />
                  ) : (
                    <ChevronDown size={18} />
                  )}
                </button>
              </div>

              {/* SAFETY DETAILS */}
              {showSafety && (
                <div className="mt-4 bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-[2px]" />
                      <span>Delivery partners are verified & trained</span>
                    </li>

                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-[2px]" />
                      <span>Contactless delivery option</span>
                    </li>

                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-[2px]" />
                      <span>Emergency support during delivery</span>
                    </li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {/* DELIVERY INSTRUCTION */}

        <DeliveryInstructions
         isAdminView={isAdminView}
          isDelivered={isDelivered}
          instructionList={instructionList}
          submittedInstruction={submittedInstruction}
          existingInstruction={existingInstruction}
          fetchInstructions={fetchDeliveryInstructions}
          submitInstruction={submitInstruction}
          taskId={taskId}
        />

        {/* DELIVERY Details */}
        <div className="bg-white max-w-md mt-2 mb-6 mx-4 rounded-xl shadow p-4 space-y-4">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-gray-100">
              {deliveryDetails?.header?.icon &&
                iconMap?.[deliveryDetails.header.icon]}
            </div>
            <div>
              <p className="font-semibold">{deliveryDetails.header.title}</p>
              <p className="text-sm text-gray-500">
                {deliveryDetails.header.subtitle}
              </p>
            </div>
          </div>

          <hr />

          {/* SECTIONS */}
          {deliveryDetails.sections.map((item, index) => (
            <div key={item.id}>
              <div className="flex items-start gap-3">
                <div className="p-3 rounded-full bg-gray-100">
                  {item?.icon && iconMap?.[item.icon]}
                </div>
                <div>
                  <p className="font-semibold">{item.title}</p>
                  {item.description && (
                    <p className="text-sm text-gray-600">{item.description}</p>
                  )}
                </div>
              </div>

              {index !== deliveryDetails.sections.length - 1 && (
                <hr className="mt-4" />
              )}
            </div>
          ))}
        </div>

        {/* RATING SECTION */}
        <DeliveryRating
         isAdminView={isAdminView}
          isDelivered={isDelivered}
          showRatingSection={showRatingSection}
          existingRating={existingRating}
          loadingRating={loadingRating}
          driverRating={driverRating}
          setDriverRating={setDriverRating}
          customerMessage={customerMessage}
          setCustomerMessage={setCustomerMessage}
          ratingSubmitting={ratingSubmitting}
          submitDeliveryRating={handleSubmitRating}
          ratingDismissed={ratingDismissed}
          dismissRating={dismissRating}
        />

        {/* ORDER SUMMARY */}
        <OrderSummary
          isDelivered={isDelivered}
          orderId={orderId}
          products={products}
          pricing={pricing}
          merchantStatus={merchantStatus}
          fetchOrderDetails={fetchOrderDetails}
        />
      </div>
    </div>
  );
}
